// This component allows browsing and importing Google Drive files
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Loader2, Search, FileText, FileSpreadsheet, File, RefreshCw, Import } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'

// Define the file type
interface DriveFile {
  id: string
  name: string
  mimeType: string
}

// Define the response type
interface DriveFilesResponse {
  files: DriveFile[]
  nextPageToken?: string
}

export function GoogleDriveFileBrowser() {
  const { user } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [pageToken, setPageToken] = useState<string | null>(null)
  const [importingFileId, setImportingFileId] = useState<string | null>(null)
  
  // Query to check if user has connected Google Drive
  const { data: connection, isLoading: isLoadingConnection } = useQuery({
    queryKey: ['googleDriveConnection', user?.id],
    queryFn: async () => {
      if (!user) return null
      
      const { data, error } = await supabase
        .from('google_drive_connections')
        .select('id, google_email')
        .eq('user_id', user.id)
        .single()
        
      if (error && error.code !== 'PGRST116') {
        throw error
      }
      
      return data
    },
    enabled: !!user,
  })
  
  // Query to fetch files from Google Drive
  const { data: filesData, isLoading: isLoadingFiles, refetch } = useQuery({
    queryKey: ['googleDriveFiles', user?.id, searchQuery, pageToken],
    queryFn: async () => {
      if (!user || !connection) return null
      
      const params: Record<string, string> = {}
      if (searchQuery) {
        params.query = searchQuery
      }
      if (pageToken) {
        params.pageToken = pageToken
      }
      
      // Only fetch text-based files
      params.mimeType = [
        'text/plain',
        'text/markdown',
        'application/vnd.google-apps.document',
        'application/vnd.google-apps.spreadsheet',
      ].join(',')
      
      const { data, error } = await supabase.functions.invoke('list-drive-files', {
        method: 'POST',
        body: params,
      })
      
      if (error) throw error
      return data as DriveFilesResponse
    },
    enabled: !!user && !!connection,
  })
  
  // Mutation to import a file from Google Drive
  const importMutation = useMutation({
    mutationFn: async (fileId: string) => {
      if (!user) throw new Error('User not authenticated')
      
      const { data, error } = await supabase.functions.invoke('import-drive-file', {
        method: 'POST',
        body: { fileId },
      })
      
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      toast({
        title: 'File imported successfully',
        description: 'The file has been imported as a new Posit.',
      })
      
      // Navigate to the new posit
      if (data.posit && data.posit.id) {
        navigate(`/posits/${data.posit.id}`)
      }
    },
    onError: (error) => {
      toast({
        title: 'Error importing file',
        description: 'There was an error importing the file. Please try again.',
        variant: 'destructive',
      })
    },
    onSettled: () => {
      setImportingFileId(null)
    },
  })
  
  // Function to handle file import
  const handleImportFile = (fileId: string) => {
    setImportingFileId(fileId)
    importMutation.mutate(fileId)
  }
  
  // Function to handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPageToken(null)
    refetch()
  }
  
  // Function to handle next page
  const handleNextPage = () => {
    if (filesData?.nextPageToken) {
      setPageToken(filesData.nextPageToken)
    }
  }
  
  // Function to handle previous page
  const handlePreviousPage = () => {
    setPageToken(null)
  }
  
  // Function to get file icon based on MIME type
  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/vnd.google-apps.document') {
      return <FileText className="h-4 w-4" />
    } else if (mimeType === 'application/vnd.google-apps.spreadsheet') {
      return <FileSpreadsheet className="h-4 w-4" />
    } else {
      return <File className="h-4 w-4" />
    }
  }
  
  // Function to get file type label based on MIME type
  const getFileTypeLabel = (mimeType: string) => {
    if (mimeType === 'application/vnd.google-apps.document') {
      return 'Google Doc'
    } else if (mimeType === 'application/vnd.google-apps.spreadsheet') {
      return 'Google Sheet'
    } else if (mimeType === 'text/plain') {
      return 'Text'
    } else if (mimeType === 'text/markdown') {
      return 'Markdown'
    } else {
      return 'File'
    }
  }
  
  if (isLoadingConnection) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Google Drive Files</CardTitle>
          <CardDescription>Loading connection status...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    )
  }
  
  if (!connection) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Google Drive Files</CardTitle>
          <CardDescription>Connect your Google Drive to browse files</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center text-muted-foreground">
            <p>You need to connect your Google Drive account first.</p>
            <p className="mt-2">Go to the Google Drive Integration section to connect.</p>
          </div>
        </CardContent>
      </Card>
    )
  }
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Google Drive Files</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => refetch()}
            disabled={isLoadingFiles}
          >
            <RefreshCw className={`h-4 w-4 ${isLoadingFiles ? 'animate-spin' : ''}`} />
            <span className="sr-only">Refresh</span>
          </Button>
        </CardTitle>
        <CardDescription>
          Browse and import files from your Google Drive
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSearch} className="flex space-x-2 mb-4">
          <Input
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1"
          />
          <Button type="submit" disabled={isLoadingFiles}>
            <Search className="h-4 w-4 mr-2" />
            Search
          </Button>
        </form>
        
        <ScrollArea className="h-[400px] rounded-md border">
          {isLoadingFiles ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-12 rounded-md" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : filesData?.files && filesData.files.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filesData.files.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell className="font-medium flex items-center gap-2">
                      {getFileIcon(file.mimeType)}
                      <span className="truncate max-w-[300px]">{file.name}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getFileTypeLabel(file.mimeType)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => handleImportFile(file.id)}
                        disabled={importingFileId === file.id}
                      >
                        {importingFileId === file.id ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Import className="mr-2 h-4 w-4" />
                            Import
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-8">
              <p className="text-muted-foreground">No files found</p>
              {searchQuery && (
                <p className="text-sm text-muted-foreground mt-2">
                  Try a different search term or clear the search
                </p>
              )}
            </div>
          )}
        </ScrollArea>
      </CardContent>
      {filesData?.files && filesData.files.length > 0 && (
        <CardFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePreviousPage}
            disabled={!pageToken || isLoadingFiles}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={handleNextPage}
            disabled={!filesData.nextPageToken || isLoadingFiles}
          >
            Next
          </Button>
        </CardFooter>
      )}
    </Card>
  )
} 